import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { ApiHelperService } from './api-helper.service';
import { GenericAPIArrayResponse, GenericAPIResponse } from '../models/api-response';
import { PopupDialogDefinition } from '../models/popup-dialog';
import { QWIKBOX_STORAGE_BASE_URL } from '../constants/storage.constants';

@Injectable({ providedIn: 'root' })
export class PopupDialogService {
  constructor(
    private http: HttpClient,
    private api: ApiHelperService
  ) {}

  list(): Promise<GenericAPIArrayResponse<PopupDialogDefinition>> {
    return lastValueFrom(
      this.http.get<GenericAPIArrayResponse<PopupDialogDefinition>>(
        `${this.api.apiUrl()}/admin/popup-dialogs`,
        { headers: this.api.apiHeaderWithToken() }
      )
    );
  }

  eligible(): Promise<GenericAPIArrayResponse<PopupDialogDefinition>> {
    return lastValueFrom(
      this.http.get<GenericAPIArrayResponse<PopupDialogDefinition>>(
        `${this.api.apiUrl()}/popup-dialogs/eligible?platform=desktop`,
        { headers: this.api.apiHeaderWithToken() }
      )
    );
  }

  save(dialog: PopupDialogDefinition): Promise<GenericAPIResponse<PopupDialogDefinition>> {
    const url = `${this.api.apiUrl()}/admin/popup-dialogs${dialog._id ? `/${dialog._id}` : ''}`;
    const options = { headers: this.api.apiHeaderWithToken() };
    return lastValueFrom(
      dialog._id
        ? this.http.put<GenericAPIResponse<PopupDialogDefinition>>(url, dialog, options)
        : this.http.post<GenericAPIResponse<PopupDialogDefinition>>(url, dialog, options)
    );
  }

  setActive(
    dialog: PopupDialogDefinition,
    active: boolean
  ): Promise<GenericAPIResponse<PopupDialogDefinition>> {
    return lastValueFrom(
      this.http.put<GenericAPIResponse<PopupDialogDefinition>>(
        `${this.api.apiUrl()}/admin/popup-dialogs/${dialog._id}`,
        { active },
        { headers: this.api.apiHeaderWithToken() }
      )
    );
  }

  remove(id: string): Promise<GenericAPIResponse<PopupDialogDefinition>> {
    return lastValueFrom(
      this.http.delete<GenericAPIResponse<PopupDialogDefinition>>(
        `${this.api.apiUrl()}/admin/popup-dialogs/${id}`,
        { headers: this.api.apiHeaderWithToken() }
      )
    );
  }

  async uploadImage(file: File): Promise<string> {
    if (
      !['image/jpeg', 'image/png', 'image/webp'].includes(file.type) ||
      !file.size ||
      file.size > 5 * 1024 * 1024
    ) {
      throw new Error('Choose a JPEG, PNG, or WebP image no larger than 5 MB.');
    }
    const response = await lastValueFrom(
      this.http.post<GenericAPIResponse<{ imageKey: string; uploadUrl: string }>>(
        `${this.api.apiUrl()}/admin/popup-dialogs/image-upload`,
        { contentType: file.type, size: file.size },
        { headers: this.api.apiHeaderWithToken() }
      )
    );
    if (!response.success || !response.result)
      throw new Error(response.message || 'Unable to upload the image.');
    await lastValueFrom(
      this.http.put(response.result.uploadUrl, file, {
        headers: { 'Content-Type': file.type },
        responseType: 'text'
      })
    );
    return response.result.imageKey;
  }

  imageUrl(key: string): string {
    return key ? `${QWIKBOX_STORAGE_BASE_URL}/${key}` : '';
  }
}
